import { waitForMs } from 'libs/end-to-end/src/generic';
import { LoggerPayload } from 'libs/end-to-end/src/isomorphic-logger';

export function withRetrier<
  Args extends {
    logger: LoggerPayload;
  },
  Return,
>({
  maxAttempts,
  fn,
  criteriaFn,
  notifyFn,
  fixedDelayPerAttempt,
  maxJitterPerAttempt,
}: {
  maxAttempts: number;
  fn: (operationParameters: Args) => Promise<Return>;
  criteriaFn: (result: Return) => boolean;
  notifyFn?: (props: {
    result: Return;
    attempt: number;
    maxAttempts: number;
    logger: LoggerPayload;
  }) => void;
  fixedDelayPerAttempt?: number;
  maxJitterPerAttempt?: number;
}): (operationParameters: Args) => Promise<Return> {
  const evalFixedDelayPerAttempt =
    fixedDelayPerAttempt === undefined ? 5000 : fixedDelayPerAttempt;
  const evalMaxJitterPerAttempt =
    maxJitterPerAttempt === undefined ? 1000 : maxJitterPerAttempt;

  return async (operationParameters: Args) => {
    let attempt = 1;

    while (true) {
      const result = await fn(operationParameters);

      const shouldRetry = attempt < maxAttempts && criteriaFn(result);

      if (shouldRetry) {
        if (notifyFn) {
          // Just make sure to copy because of the loop reference.
          notifyFn({
            result,
            attempt,
            maxAttempts,
            logger: operationParameters.logger,
          });
        }

        const jitter = Math.round(Math.random() * evalMaxJitterPerAttempt); // 0-{evalMaxJitterPerAttempt}ms
        const interval = evalFixedDelayPerAttempt * attempt + jitter;
        await waitForMs(interval);
        attempt++;
        continue;
      }

      return result;
    }
  };
}
